import React, { useCallback } from 'react';

import {
  ArrayField,
  ArrayInput,
  Datagrid,
  Form,
  SelectField,
  SelectInput,
  SimpleFormIterator,
  required,
  useNotify,
  useRecordContext,
  useUpdate,
} from 'react-admin';

import {
  Card,
  CardHeader,
  Divider,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
} from '@mui/material';

import { FieldValues } from 'react-hook-form';

import CreateIcon from '@mui/icons-material/Create';
import useDialogStatus from '../hooks/useDialogStatus';

import { boldDataGridStyle } from '../constants/style/datagridStyles';
import NoResults from '../components/layout/NoResults';
import { entityTypesChoices } from '../constants/entityTypes';
import { displayProductPartyRolesChoices, getProductPartyRolesChoices } from '../constants/productPartyRoles';
import ReferenceEntityField from './ReferenceEntityField';
import { IProduct } from '../utilities/schemas/product';
import ReferenceEntityInputV2 from './ReferenceEntityInputV2';
import EntityDisplayNameField from './EntityDisplayNameField';
import useHiddenUntilHover from '../hooks/useHiddenUntilHover';
import { ProductTypes } from '../constants/productTypes';

export const ProductPartiesInputFields = ({
  source,
  productType,
}: {
  source?: string;
  productType: ProductTypes;
}) => (
  <ArrayInput source={source ?? 'parties'} label={false} fullWidth defaultValue={[{}]}>
    <SimpleFormIterator inline sx={{ '.RaSimpleFormIterator-form': { marginY: 2 } }} disableReordering>
      <ReferenceEntityInputV2 source="" validate={[required()]} label="entity" />
      <SelectInput source="role" helperText={false} validate={[required()]} choices={getProductPartyRolesChoices(productType)} />
    </SimpleFormIterator>
  </ArrayInput>
);

const EditDialog = ({
  showEdit,
  buttonId,
}: {
  showEdit: boolean;
  buttonId: string;
}) => {
  const notify = useNotify();
  const record = useRecordContext<IProduct>();
  const { open, openDialog, closeDialog } = useDialogStatus();

  const [update] = useUpdate();

  const handelSave = useCallback((data: FieldValues) => {
    update('products', { id: record?.id, data }, {
      onError: () => notify('Could not update parties', { type: 'error' }),
      onSuccess: closeDialog,
      mutationMode: 'pessimistic',
    });
  }, [closeDialog, record?.id, notify, update]);

  if (!showEdit || !record) return null;
  const productPartyRolesChoices = getProductPartyRolesChoices(record.productType);

  return (
    <>
      <IconButton size="small" onClick={openDialog} id={buttonId}>
        <CreateIcon color="secondary" fontSize="small" />
      </IconButton>
      <Dialog open={open} onClose={closeDialog} maxWidth="lg">
        <Form
          record={{
            // TODO: Remove the filter once the old roles are not used anymore
            parties: record?.parties?.filter((party) => productPartyRolesChoices.some(
              (choice) => choice.id === party.role,
            )),
          }}
          onSubmit={handelSave}
        >
          <DialogTitle>Parties</DialogTitle>
          <DialogContent>
            <ProductPartiesInputFields productType={record.productType} />
          </DialogContent>
          <DialogActions>
            <Button color="error" onClick={closeDialog}>Cancel</Button>
            <Button type="submit" variant="contained">Save</Button>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
};

export const ProductPartiesDataGrid = () => (
  <Datagrid sx={boldDataGridStyle} bulkActionButtons={false} empty={<NoResults text="No data to display." icon={null} />}>
    <SelectField choices={entityTypesChoices} source="type" />
    <ReferenceEntityField source="">
      <EntityDisplayNameField />
    </ReferenceEntityField>
    <SelectField choices={displayProductPartyRolesChoices} source="role" />
  </Datagrid>
);

const ProductParties = ({ showEdit }: { showEdit: boolean }) => {
  const { hiddenElementId, wrapperSx } = useHiddenUntilHover();
  return (
    <Card
      variant="outlined"
      sx={wrapperSx}
    >
      <CardHeader
        sx={{
          color: 'black',
          fontWeight: 'bold',
          flexWrap: 'wrap',
        }}
        slotProps={{ title: { variant: 'inherit' } }}
        title="Parties"
        action={<EditDialog showEdit={showEdit} buttonId={hiddenElementId} />}
      />
      <Divider />
      <ArrayField source="parties">
        <ProductPartiesDataGrid />
      </ArrayField>
    </Card>
  );
};

export default ProductParties;
